<script>
export default {
    page() {
        return { title: 'Search Your Library' } // title only — noindex page
    },
}
</script>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { LIBRARY_ROUTES } from '@utils/library-routes'
import { debounce } from '@utils/performance'
import Layout from '@layouts/main-layout'
import LibrarySearchResults from '@components/library/library-search-results'
import libraryRecentSearch from '@services/library-recent-search-service'

const route = useRoute()
const router = useRouter()

// the recent-search service stores oldest-first (it appends); the view renders newest-first
const newestFirst = recents => recents.slice().reverse()

const query = ref(route.query.q || '')
const recentSearches = ref(newestFirst(libraryRecentSearch.getRecent()))

const hasQuery = computed(() => query.value.trim().length > 0)

const syncQueryParam = debounce(() => {
    // the timer survives unmount (debounce has no cancel) — the reactive route is live
    // post-navigation, so this no-ops instead of bouncing the user back to search
    if (route.name !== LIBRARY_ROUTES.search) return
    const q = query.value.trim()
    if ((route.query.q || '') === q) return
    router.replace({ name: LIBRARY_ROUTES.search, query: q ? { q } : {} })
}, 300)

watch(query, () => syncQueryParam())

function removeRecent(item) {
    recentSearches.value = newestFirst(libraryRecentSearch.removeFromRecent(item))
}

function openRecent(item) {
    router.push({ name: item.name, params: item.params })
}

function cancel() {
    if (window.history.length > 1) {
        router.back()
    } else {
        router.push({ name: LIBRARY_ROUTES.landing }) // direct/bookmarked entry has no in-app history
    }
}
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div class="search-row">
                    <div class="field">
                        <BaseSearchInput
                            v-model="query"
                            placeholder="Filter Your Library"
                            variant="filter"
                            :debounce-delay="0"
                            autofocus
                        />
                    </div>
                    <button
                        class="cancel"
                        type="button"
                        @click="cancel"
                    >
                        Cancel
                    </button>
                </div>
                <template v-if="!hasQuery">
                    <template v-if="recentSearches.length">
                        <h2 class="group-title">Recent searches</h2>
                        <div
                            v-for="item in recentSearches"
                            :key="`${item.type}-${item.id}`"
                            class="recent-row"
                        >
                            <button
                                class="recent-label"
                                type="button"
                                @click="openRecent(item)"
                            >
                                {{ item.title }}
                            </button>
                            <BaseIcon
                                class="remove"
                                name="x"
                                @click.native="removeRecent(item)"
                            />
                        </div>
                    </template>
                    <BaseEmptyList
                        v-else
                        :error="{
                            title: 'Find your favorites',
                            paragraph: `Search everything you've liked or followed`,
                        }"
                    />
                </template>
                <LibrarySearchResults
                    v-else
                    :query="query"
                />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.search-row {
    display: flex;
    gap: $s-s;
    align-items: center;
    margin-bottom: $s-l;
}

.field {
    flex: 1;
    // unboxed bar with a bottom rule (Figma 897-550) — same treatment as the header field
    padding: $s-xxs $s-s $s-s;
    border-bottom: 1px solid $c-boulder-grey;

    @include desktop {
        max-width: 32rem;
    }

}

.cancel {
    color: $c-text-faded;
    cursor: pointer;
    background: none;
    border: none;
}

.group-title {
    @include sectionHeading;

    margin: $s-l 0 $s-s;
}

.recent-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $s-xs 0;
}

.recent-label {
    color: $c-white;
    cursor: pointer;
    background: none;
    border: none;
}

.remove {
    width: 1rem;
    height: 1rem;   // BaseIcon svgs have no intrinsic size — omit height and they default to 150px
    cursor: pointer;
    fill: $c-text-faded;
}
</style>
